import { Colors, Text } from '@amelio/ui-library';
import { useRef } from 'react';

import { RetryButton } from './RetryButton';

import type { MeetingTranscriptLoadFailure } from '../types';

import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';

interface TranscriptReadingPanelProps {
    failure?: MeetingTranscriptLoadFailure;
    disabled: boolean;
}

/**
 * The transcript being read, then — if the read fails — the way to read it again. One status region
 * spans both, busy while the read is under way, so the failure is announced where the wait was. The
 * retry sits beside the region rather than in it: a live region recites everything it holds.
 */
export const TranscriptReadingPanel = ({ failure, disabled }: TranscriptReadingPanelProps) => {
    const statusRef = useRef<HTMLDivElement>(null);

    return (
        <div className={'flex w-full p-6'}>
            <div
                className={
                    failure
                        ? 'flex w-full flex-col gap-3 rounded-xl bg-white/80 px-6 py-5'
                        : 'w-full'
                }
            >
                <div
                    ref={statusRef}
                    role={'status'}
                    tabIndex={-1}
                    aria-busy={!failure}
                    className={'w-full focus:outline-none'}
                >
                    {failure ? (
                        <Text
                            size={'small'}
                            color={Colors.BASE_600}
                        >
                            {failure.message}
                        </Text>
                    ) : (
                        <Skeleton className={'flex w-full'}>
                            <div
                                className={'w-full space-y-3'}
                                data-testid={'meeting-transcript-loading'}
                            >
                                <Bone className={'h-4 w-1/3'} />
                                <Bone className={'h-40 w-full'} />
                            </div>
                        </Skeleton>
                    )}
                </div>

                {failure && (
                    <RetryButton
                        label={failure.retryLabel}
                        onRetry={failure.onRetry}
                        statusRef={statusRef}
                        isRetrying={failure.isRetrying}
                        disabled={disabled}
                        dataTestId={'retry-transcript-button'}
                    />
                )}
            </div>
        </div>
    );
};
